import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Package, CheckCircle, XCircle, Save } from 'lucide-react';

const ZOBOX_BLUE = '#2b3380';

const OQCForm = ({ user, onLogout }) => {
  const [deviceCode, setDeviceCode] = useState('');
  const [checks, setChecks] = useState({
    charged_70: null,
    device_clean: null,
    imei_label: null,
    no_fingerprints: null,
    screen_protector: null,
    box_condition: null,
    accessories_included: null,
    physical_damage: null
  });
  const [notes, setNotes] = useState('');

  const checkItems = [
    { key: 'charged_70', label: 'Device Charged to 70% or More', icon: '🔋' },
    { key: 'device_clean', label: 'Device Cleaned (No Dust/Dirt)', icon: '✨' },
    { key: 'imei_label', label: 'IMEI Label Stuck Properly', icon: '🏷️' },
    { key: 'no_fingerprints', label: 'No Fingerprint Marks on Screen/Body', icon: '👆' },
    { key: 'screen_protector', label: 'Screen Protector Applied (if applicable)', icon: '🛡️' },
    { key: 'box_condition', label: 'Packaging Box in Good Condition', icon: '📦' },
    { key: 'accessories_included', label: 'All Accessories Included', icon: '🎁' },
    { key: 'physical_damage', label: 'No New Physical Damage', icon: '🔍' }
  ];

  const handleCheckChange = (key, value) => {
    setChecks({ ...checks, [key]: value });
  };

  const handleSubmit = async () => {
    // Check if all items are checked
    const allChecked = Object.values(checks).every(val => val !== null);
    if (!allChecked) {
      alert('Please complete all OQC checks before submitting!');
      return;
    }

    // Check if any failed
    const hasFailed = Object.values(checks).some(val => val === false);
    
    if (hasFailed) {
      if (!window.confirm('Some checks failed. Device may not be ready for dispatch. Continue?')) {
        return;
      }
    }

    // TODO: Submit to backend
    alert('OQC completed successfully! Device ready for dispatch.');
  };

  const allPassed = Object.keys(checks).length > 0 && Object.values(checks).every(val => val === true);
  const hasFailed = Object.values(checks).some(val => val === false);
  const progress = Object.values(checks).filter(val => val !== null).length;
  const total = checkItems.length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Package className="w-8 h-8 mr-3" />
            OQC - Outgoing Quality Check
          </h1>
          <p className="text-gray-600 mt-1">
            Final look & feel inspection before dispatch • Simple checklist
          </p>
        </div>

        {/* Device Code Input */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Device Code / Serial Number
          </label>
          <input
            type="text"
            value={deviceCode}
            onChange={(e) => setDeviceCode(e.target.value)}
            placeholder="Scan or enter device code"
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
          />
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Progress</span>
            <span className="text-sm font-semibold" style={{ color: ZOBOX_BLUE }}>
              {progress}/{total} checks completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${(progress / total) * 100}%`,
                backgroundColor: allPassed ? '#22c55e' : hasFailed ? '#ef4444' : ZOBOX_BLUE
              }}
            />
          </div>
        </div>

        {/* OQC Checklist */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            OQC Checklist
          </h2>
          <div className="space-y-4">
            {checkItems.map((item) => (
              <div
                key={item.key}
                className={`p-4 rounded-lg border-2 transition-all ${
                  checks[item.key] === true
                    ? 'bg-green-50 border-green-300'
                    : checks[item.key] === false
                    ? 'bg-red-50 border-red-300'
                    : 'bg-gray-50 border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-2xl">{item.icon}</span>
                    <span className="font-medium text-gray-800">{item.label}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCheckChange(item.key, true)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                        checks[item.key] === true
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-green-400'
                      }`}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleCheckChange(item.key, false)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                        checks[item.key] === false
                          ? 'bg-red-600 text-white'
                          : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-red-400'
                      }`}
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Additional Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any issues or observations..."
            rows="4"
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Status Message */}
        {progress === total && (
          <div
            className={`rounded-lg p-6 border-2 ${
              allPassed
                ? 'bg-green-50 border-green-300'
                : 'bg-red-50 border-red-300'
            }`}
          >
            <div className="flex items-center gap-3">
              {allPassed ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
              <div>
                <h3 className={`font-bold text-lg ${
                  allPassed ? 'text-green-900' : 'text-red-900'
                }`}>
                  {allPassed ? '✅ All Checks Passed!' : '❌ Some Checks Failed'}
                </h3>
                <p className={`text-sm ${
                  allPassed ? 'text-green-700' : 'text-red-700'
                }`}>
                  {allPassed
                    ? 'Device is ready for dispatch to customer'
                    : 'Device may need attention before dispatch'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!deviceCode || progress < total}
          className={`w-full py-4 rounded-lg font-bold text-white text-lg transition-all ${
            !deviceCode || progress < total
              ? 'bg-gray-300 cursor-not-allowed'
              : allPassed
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-orange-600 hover:bg-orange-700'
          }`}
        >
          <Save className="w-6 h-6 inline-block mr-2" />
          {allPassed ? 'Complete OQC & Mark Ready for Dispatch' : 'Complete OQC with Issues'}
        </button>
      </div>
    </Layout>
  );
};

export default OQCForm;
